import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, Search, Calendar, Users, ArrowLeft, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CareerApplicationsAdmin() {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      loadApplications();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', response.data.token);
      setIsAuthenticated(true);
      await loadApplications();
    } catch (error) {
      setLoginError('Invalid password. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      filterApplications();
    }
  }, [applications, searchTerm, isAuthenticated]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/career`);
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        app.full_name.toLowerCase().includes(search) ||
        app.email.toLowerCase().includes(search) ||
        app.position_interest.toLowerCase().includes(search)
      );
    }

    setFilteredApplications(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Position', 'Experience', 'Additional Info'];
    const rows = filteredApplications.map(a => [
      new Date(a.timestamp).toLocaleDateString(),
      a.full_name,
      a.email,
      a.phone,
      a.position_interest,
      a.experience.replace(/\n/g, ' '),
      a.additional_info.replace(/\n/g, ' ')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `career-applications-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gray-900 border border-green-500/30 rounded-lg p-8">
          <div className="text-center mb-8">
            <Lock className="text-green-400 mx-auto mb-4" size={48} />
            <h2 className="text-3xl font-black text-gray-100">Career Applications Admin</h2>
            <p className="text-gray-400 mt-2">Enter password to access applications</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label className="block text-gray-300 font-semibold mb-2">Admin Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                placeholder="Enter password"
                required
              />
            </div>

            {loginError && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pt-4 pb-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Sticky Navigation */}
        <div className="sticky top-4 z-50 bg-gray-800 border border-green-500/30 rounded-lg p-4 mb-8 shadow-xl" style={{backdropFilter: 'blur(10px)', backgroundColor: 'rgba(31, 41, 55, 0.95)'}}>
          <div className="flex gap-3 flex-wrap justify-center">
            <a href="/admin" className="bg-gray-700 hover:bg-gray-600 text-green-400 px-4 py-2 rounded-lg font-bold transition-all text-sm">
              Content Editor
            </a>
            <a href="/admin/prize-draws" className="text-gray-900 px-4 py-2 rounded-lg font-bold transition-all text-sm" style={{backgroundColor: '#01F9C6'}}>
              Prize Draws
            </a>
            <a href="/admin/quote-requests" className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg font-bold transition-all text-sm">
              Quote Requests
            </a>
            <a href="/admin/career-applications" className="bg-purple-500 hover:bg-purple-400 text-gray-900 px-4 py-2 rounded-lg font-bold transition-all text-sm">
              Career Applications
            </a>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <a 
            href="/admin"
            className="inline-flex items-center text-green-400 hover:text-green-300 mb-4 transition-all"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Admin Panel
          </a>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-black text-gray-100 mb-2">Career Applications</h1>
              <p className="text-gray-400">Total: <span className="text-green-400 font-bold">{applications.length}</span> | Filtered: <span className="text-yellow-400 font-bold">{filteredApplications.length}</span></p>
            </div>
            <button
              onClick={exportToCSV}
              className="bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2"
            >
              <Download size={20} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 bg-gray-800 border border-green-500/30 rounded-lg p-6">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-gray-300 font-semibold mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-500" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or position..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-gray-800 border border-green-500/30 rounded-lg overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading applications...</div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No applications found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Phone</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Position</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-green-400">Experience</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 text-gray-300 text-sm">
                        {new Date(app.timestamp).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-gray-100 font-semibold">
                        {app.full_name}
                      </td>
                      <td className="px-6 py-4 text-gray-100">
                        <a href={`mailto:${app.email}`} className="text-green-400 hover:text-green-300">
                          {app.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-gray-100 font-mono text-sm">
                        {app.phone}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">
                          {app.position_interest}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300 text-sm max-w-xs truncate">
                        {app.experience}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Total Applications</p>
                <p className="text-3xl font-bold text-green-400">{applications.length}</p>
              </div>
              <Users className="text-green-400" size={40} />
            </div>
          </div>

          <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">This Month</p>
                <p className="text-3xl font-bold text-yellow-400">
                  {applications.filter(a => {
                    const appDate = new Date(a.timestamp);
                    const now = new Date();
                    return appDate.getMonth() === now.getMonth() && 
                           appDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <Calendar className="text-yellow-400" size={40} />
            </div>
          </div>

          <div className="bg-gray-800 border border-green-500/30 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Filtered Results</p>
                <p className="text-3xl font-bold text-purple-400">{filteredApplications.length}</p>
              </div>
              <Search className="text-purple-400" size={40} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CareerApplicationsAdmin;
